import { DRUG_SCREEN_STATUS } from 'utils/constants'
import { type PatientDrugScreenStatusStaffApiOutContract } from 'api/generated/pwrdbyQuickMDProvidersApi.schemas'

type DrugScreen = PatientDrugScreenStatusStaffApiOutContract

/**
 * Color treatment for the drug screen card on the visit page.
 * - error   → red surface, used for the highly-visible compliance gaps (none on file, expired)
 * - warning → yellow surface, used for "due soon"
 * - neutral → white surface, used for compliant / informational states
 */
export const DRUG_SCREEN_CARD_VARIANT = {
  error: 'error',
  warning: 'warning',
  neutral: 'neutral',
} as const

export type DrugScreenCardVariant = (typeof DRUG_SCREEN_CARD_VARIANT)[keyof typeof DRUG_SCREEN_CARD_VARIANT]

/**
 * The detail line under the "Drug screen" title is one of two shapes:
 * - status → a colored icon + label (e.g. ✕ None on file, ⚠ Expired)
 * - date   → a "label + completed date" pair (e.g. Completed 05/15/2025)
 */
type StatusDetail = {
  kind: 'status'
  statusIcon: 'close' | 'triangle'
  label: string
}

type DateDetail = {
  kind: 'date'
  label: string
}

export type DrugScreenComplianceDisplay = {
  cardVariant: DrugScreenCardVariant
  mainIcon: 'vial' | 'vialCheck'
  detail: StatusDetail | DateDetail
}

const NONE_ON_FILE: DrugScreenComplianceDisplay = {
  cardVariant: DRUG_SCREEN_CARD_VARIANT.error,
  mainIcon: 'vial',
  detail: { kind: 'status', statusIcon: 'close', label: 'None on file' },
}

/**
 * Derives the color-coded compliance display for a patient's drug screen from
 * its status. The status is the source of truth — it is never manually entered.
 */
export function getDrugScreenComplianceDisplay(drugScreen?: DrugScreen): DrugScreenComplianceDisplay {
  if (!drugScreen?.Status) {
    return NONE_ON_FILE
  }

  switch (drugScreen.Status) {
    case DRUG_SCREEN_STATUS.due:
      return NONE_ON_FILE
    case DRUG_SCREEN_STATUS.expired:
      return {
        cardVariant: DRUG_SCREEN_CARD_VARIANT.error,
        mainIcon: 'vial',
        detail: { kind: 'status', statusIcon: 'triangle', label: 'Expired' },
      }
    case DRUG_SCREEN_STATUS.duesoon:
      return {
        cardVariant: DRUG_SCREEN_CARD_VARIANT.warning,
        mainIcon: 'vial',
        detail: { kind: 'status', statusIcon: 'triangle', label: 'Due soon' },
      }
    case DRUG_SCREEN_STATUS.verified:
      return {
        cardVariant: DRUG_SCREEN_CARD_VARIANT.neutral,
        mainIcon: 'vialCheck',
        detail: { kind: 'date', label: 'Completed' },
      }
    case DRUG_SCREEN_STATUS.review:
      return {
        cardVariant: DRUG_SCREEN_CARD_VARIANT.neutral,
        mainIcon: 'vial',
        detail: { kind: 'date', label: 'In review' },
      }
    case DRUG_SCREEN_STATUS.exempt:
      return {
        cardVariant: DRUG_SCREEN_CARD_VARIANT.neutral,
        mainIcon: 'vialCheck',
        detail: { kind: 'date', label: 'Exempt' },
      }
    default:
      // Only the statuses above are valid drug screen compliance states. Any
      // unrecognized value is treated as nothing on file so the gap stays visible.
      return NONE_ON_FILE
  }
}
